/**
 * InfoButton Component
 * A reusable button for showing help (?) or alert (!) information
 */

import React from 'react';
import { HelpCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '../../utils/cn';

export type InfoButtonVariant = 'help' | 'alert' | 'info';

interface InfoButtonProps {
  variant?: InfoButtonVariant;
  isActive?: boolean;
  onClick?: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const variantConfig = {
  help: {
    icon: HelpCircle,
    activeClass: 'bg-blue-100 text-blue-600',
    hoverClass: 'hover:bg-gray-200 text-gray-500',
    defaultTitle: 'Help',
  },
  alert: {
    icon: AlertCircle,
    activeClass: 'bg-orange-100 text-orange-600',
    hoverClass: 'hover:bg-gray-200 text-gray-500',
    defaultTitle: 'Important',
  },
  info: {
    icon: Info,
    activeClass: 'bg-blue-100 text-blue-600',
    hoverClass: 'hover:bg-gray-200 text-gray-500',
    defaultTitle: 'Information',
  },
};

const sizeConfig = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

export const InfoButton: React.FC<InfoButtonProps> = ({
  variant = 'help',
  isActive = false,
  onClick,
  title,
  size = 'md',
  className,
}) => {
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'p-1 rounded transition-colors',
        isActive ? config.activeClass : config.hoverClass,
        className
      )}
      title={title || config.defaultTitle}
    >
      <Icon className={sizeConfig[size]} />
    </button>
  );
};

export default InfoButton;
